<template>
  <div class="notifications-page">
    <div class="notifications-header">
      <div class="notifications-kicker">Avisos</div>
      <div class="notifications-heading">
        Informacion importante para proveedores
      </div>
    </div>

    <div v-if="notifications.length" class="notifications-grid">
      <v-card
        v-for="item in notifications"
        :key="item.id"
        class="notification-card"
        :class="[
          `notification-card--${item.priority || 'medium'}`,
          {
            'notification-card--viewed': item.is_viewed,
            'notification-card--unviewed': !item.is_viewed,
          },
        ]"
        elevation="2"
        @click="openNotification(item)"
      >
        <div
          class="notification-card-cover"
          :class="`notification-card-cover--${item.priority || 'medium'}`"
        >
          <div class="notification-card-cover-content">
            <div class="notification-card-cover-title">
              {{ cleanTitle(item.title) }}
            </div>
          </div>
        </div>

        <div class="notification-card-body">
          <div class="notification-card-preview">
            {{ item.preview || buildPreview(item.description) }}
          </div>

          <div class="notification-card-footer">
            <div class="notification-card-footer-left">
              <span class="notification-card-date">{{ item.start_date }}</span>
            </div>

            <div class="notification-card-footer-right">
              <div class="notification-card-badges notification-card-badges--footer">
                <span
                  class="notification-badge"
                  :class="
                    item.is_viewed
                      ? 'notification-badge--viewed'
                      : 'notification-badge--unviewed'
                  "
                >
                  {{ item.is_viewed ? $t("visto") : $t("no_visto") }}
                </span>
              </div>
              <span
                v-if="getActiveFiles(item).length"
                class="notification-card-files-pill"
              >
                <v-icon small>mdi-paperclip</v-icon>
                {{ getActiveFiles(item).length }} {{ $t("adjuntos") }}
              </span>
              <v-btn
                text
                small
                class="notification-card-action"
                @click.stop="openNotification(item)"
              >
                {{ $t("ver_mas") }}
              </v-btn>
            </div>
          </div>
        </div>
      </v-card>
    </div>

    <div v-else class="notifications-empty">
      <div class="notifications-empty-icon">
        <v-icon>mdi-bell-off-outline</v-icon>
      </div>
      <div class="notifications-empty-title">{{ $t("sin_resultados") }}</div>
      <div class="notifications-empty-text">
        No hay avisos disponibles por el momento.
      </div>
    </div>

    <v-dialog v-model="showNotificationDialog" max-width="960">
      <v-card v-if="selectedNotification" class="notification-modal">
        <div
          class="notification-modal-cover"
          :class="`notification-modal-cover--${selectedNotification.priority || 'medium'}`"
        >
          <div class="notification-card-cover-content">
            <div class="notification-modal-header-row">
              <div class="notification-modal-cover-title">
                {{ cleanTitle(selectedNotification.title) }}
              </div>

              <span
                class="notification-badge"
                :class="
                  selectedNotification.is_viewed
                    ? 'notification-badge--viewed'
                    : 'notification-badge--unviewed'
                "
              >
                {{
                  selectedNotification.is_viewed ? $t("visto") : $t("no_visto")
                }}
              </span>
            </div>
          </div>

          <v-btn icon dark class="notification-modal-close" @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="notification-modal-body">
          <div class="notification-modal-description">
            {{ formatNotificationText(selectedNotification.description) }}
          </div>

          <div
            v-if="getActiveFiles(selectedNotification).length"
            class="notification-files"
          >
            <div class="notification-files-header">
              <div class="notification-files-title">
                <v-icon small>mdi-paperclip</v-icon>
                {{ $t("archivos_adjuntos") }}
              </div>
              <div class="notification-files-count">
                {{ getActiveFiles(selectedNotification).length }}
              </div>
            </div>

            <div class="notification-files-grid">
              <a
                v-for="(file, index) in getActiveFiles(selectedNotification)"
                :key="index"
                :href="filesUrl + file.path"
                target="_blank"
                class="notification-file-card"
              >
                <div
                  class="notification-file-icon"
                  :class="`notification-file-icon--${getFileType(file.path)}`"
                >
                  <v-icon>{{ getFileIcon(file.path) }}</v-icon>
                </div>
                <div class="notification-file-content">
                  <div class="notification-file-name">
                    {{ getFileName(file.path) }}
                  </div>
                  <div class="notification-file-caption">
                    {{ $t("descargar_archivo") }}
                  </div>
                </div>
                <div class="notification-file-action">
                  <v-icon small>mdi-download</v-icon>
                </div>
              </a>
            </div>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: "Notifications",
  data: () => ({
    filesUrl: `http://${window.location.hostname}:${process.env.VUE_APP_ASSET_PORT}/`,
    notifications: [],
    selectedNotification: null,
    showNotificationDialog: false,
  }),
  methods: {
    async getNotifications() {
      this.openLoader();
      try {
        const response = await fetch(
          `${this.$store.state.proveedorUrl}catalogs/notifications-supplier/feed`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${window.localStorage.token}`,
            },
          }
        );

        if ([401, 419].includes(response.status)) {
          this.handleExpiredSession();
          return;
        }

        if (!response.ok) {
          this.notifications = this.sortNotifications(
            await this.getLegacyNotifications()
          );
          return;
        }

        this.notifications = this.sortNotifications(await response.json());
      } catch (error) {
        console.error(error);
        this.notifications = this.sortNotifications(
          await this.getLegacyNotifications()
        );
      } finally {
        this.closeLoader();
      }
    },
    async markAsViewed(notification) {
      if (!notification || notification.is_viewed) return;

      try {
        const response = await fetch(
          `${this.$store.state.proveedorUrl}catalogs/notifications-supplier/${notification.id}/mark-as-viewed`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${window.localStorage.token}`,
            },
          }
        );

        if ([401, 419].includes(response.status)) {
          this.handleExpiredSession();
          return;
        }

        if (!response.ok) {
          this.setNotificationAsViewed(notification.id);
          return;
        }

        const payload = await response.json();
        const viewedAt = payload?.data?.viewed_at || new Date().toISOString();
        this.setNotificationAsViewed(notification.id, viewedAt);
      } catch (error) {
        console.error(error);
        this.setNotificationAsViewed(notification.id);
      }
    },
    async getLegacyNotifications() {
      try {
        const headers = {
          Authorization: `Bearer ${window.localStorage.token}`,
        };

        const [generalResponse, specificResponse] = await Promise.all([
          fetch(
            `${this.$store.state.proveedorUrl}catalogs/notifications-supplier/get-general`,
            {
              method: "GET",
              headers,
            }
          ),
          fetch(
            `${this.$store.state.proveedorUrl}catalogs/notifications-supplier/get-specific`,
            {
              method: "GET",
              headers,
            }
          ),
        ]);

        if (
          [401, 419].includes(generalResponse.status) ||
          [401, 419].includes(specificResponse.status)
        ) {
          this.handleExpiredSession();
          return [];
        }

        const general = generalResponse.ok ? await generalResponse.json() : [];
        const specific = specificResponse.ok
          ? await specificResponse.json()
          : [];

        return [...general, ...specific].map((item) =>
          this.normalizeLegacyNotification(item)
        );
      } catch (error) {
        console.error(error);
        return [];
      }
    },
    openNotification(notification) {
      this.selectedNotification = {
        ...notification,
      };
      this.showNotificationDialog = true;
      this.markAsViewed(notification);
    },
    closeDialog() {
      this.showNotificationDialog = false;
      this.selectedNotification = null;
    },
    cleanTitle(title) {
      return String(title || "").replace(/^Asunto:\s*/i, "");
    },
    formatNotificationText(text) {
      return String(text || "")
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/\/n|\\n|\r?\n/g, "\n");
    },
    buildPreview(text) {
      const normalized = this.formatNotificationText(text)
        .replace(/\s+/g, " ")
        .trim();

      return normalized.slice(0, 145).concat(
        normalized.length > 145 ? "..." : ""
      );
    },
    getPriorityLabel(priority) {
      const labels = {
        high: this.$t("prioridad_alta"),
        medium: this.$t("prioridad_media"),
        low: this.$t("prioridad_baja"),
      };

      return labels[priority] || this.$t("prioridad_media");
    },
    getActiveFiles(notification) {
      return (notification?.notifications_supplier_files || []).filter(
        (file) => file.status
      );
    },
    getFileName(path) {
      const fileName = String(path || "").split("/").pop() || this.$t("file");
      const sanitized = fileName.split("?")[0];
      const parts = sanitized.split(".");

      if (parts.length < 2) return sanitized;

      const extension = parts.pop().toUpperCase();
      return `${parts.join(".")} (${extension})`;
    },
    getFileType(path) {
      const extension = String(path || "")
        .split(".")
        .pop()
        .split("?")[0]
        .toLowerCase();

      if (["pdf"].includes(extension)) return "pdf";
      if (["xls", "xlsx", "csv"].includes(extension)) return "sheet";
      if (["doc", "docx"].includes(extension)) return "doc";
      if (["ppt", "pptx"].includes(extension)) return "slides";
      if (["jpg", "jpeg", "png"].includes(extension)) return "image";

      return "default";
    },
    getFileIcon(path) {
      const extension = this.getFileType(path);

      const icons = {
        pdf: "mdi-file-pdf-box",
        sheet: "mdi-file-excel",
        doc: "mdi-file-word",
        slides: "mdi-file-powerpoint",
        image: "mdi-file-image",
        default: "mdi-file-download-outline",
      };

      return icons[extension] || icons.default;
    },
    normalizeLegacyNotification(item) {
      return {
        ...item,
        priority: item.priority || "medium",
        is_viewed: Boolean(item.is_viewed),
        viewed_at: item.viewed_at || null,
        preview: item.preview || this.buildPreview(item.description),
        notifications_supplier_files: item.notifications_supplier_files || [],
      };
    },
    sortNotifications(notifications) {
      const priorityWeight = {
        high: 0,
        medium: 1,
        low: 2,
      };

      return [...notifications].sort((left, right) => {
        const viewedDiff =
          Number(Boolean(left.is_viewed)) - Number(Boolean(right.is_viewed));
        if (viewedDiff !== 0) return viewedDiff;

        const leftPriority = priorityWeight[left.priority] ?? 1;
        const rightPriority = priorityWeight[right.priority] ?? 1;
        if (leftPriority !== rightPriority) return leftPriority - rightPriority;

        const leftDate = new Date(left.start_date || left.created_at || 0).getTime();
        const rightDate = new Date(
          right.start_date || right.created_at || 0
        ).getTime();
        if (leftDate !== rightDate) return rightDate - leftDate;

        return (right.id || 0) - (left.id || 0);
      });
    },
    setNotificationAsViewed(notificationId, viewedAt = null) {
      const finalViewedAt = viewedAt || new Date().toISOString();

      this.notifications = this.sortNotifications(
        this.notifications.map((item) =>
          item.id === notificationId
            ? {
                ...item,
                is_viewed: true,
                viewed_at: finalViewedAt,
              }
            : item
        )
      );

      if (
        this.selectedNotification &&
        this.selectedNotification.id === notificationId
      ) {
        this.selectedNotification = {
          ...this.selectedNotification,
          is_viewed: true,
          viewed_at: finalViewedAt,
        };
      }
    },
  },
  async created() {
    await this.getNotifications();
  },
};
</script>

<style scoped>
.notifications-page {
  width: 100%;
  margin: 32px auto 56px;
  padding: 0 20px;
}

.notifications-header {
  margin-bottom: 20px;
  color: #f3f4f6;
}

.notifications-kicker {
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #f59e0b;
}

.notifications-heading {
  font-size: 26px;
  font-weight: 700;
  line-height: 1.2;
}

.notifications-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 20px;
  align-items: start;
}

.notification-card {
  display: flex;
  flex-direction: column;
  min-height: 360px;
  overflow: hidden;
  border-radius: 18px;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.06);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    opacity 0.2s ease,
    border-color 0.2s ease;
}

.notification-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.22) !important;
}

.notification-card--viewed {
  opacity: 0.8;
}

.notification-card--unviewed {
  border-color: rgba(255, 255, 255, 0.18);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.04);
}

.notification-card-cover,
.notification-modal-cover {
  position: relative;
  min-height: 84px;
  padding: 12px 16px 14px;
  display: flex;
  align-items: flex-end;
  background-size: cover;
}

.notification-card-cover-content {
  min-width: 0;
  flex: 1;
}

.notification-card-cover--high,
.notification-modal-cover--high {
  background: linear-gradient(160deg, #ff5f57 0%, #ff5349 42%, #ff6f68 100%);
}

.notification-card-cover--medium,
.notification-modal-cover--medium {
  background: linear-gradient(160deg, #ffb84d 0%, #ff9f0a 42%, #ffc65e 100%);
}

.notification-card-cover--low,
.notification-modal-cover--low {
  background: linear-gradient(160deg, #5ac8fa 0%, #0a84ff 42%, #74d7ff 100%);
}

.notification-card-badges,
.notification-modal-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.notification-card-badges--footer {
  justify-content: flex-end;
}

.notification-card-cover-title,
.notification-modal-cover-title {
  padding-right: 12px;
  color: #ffffff;
  font-size: 21px;
  font-weight: 700;
  line-height: 1.18;
  text-wrap: balance;
}

.notification-modal-cover-title {
  font-size: 30px;
  line-height: 1.08;
  margin-bottom: 0;
}

.notification-modal-header-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.notification-modal-header-row .notification-badge {
  flex: 0 0 auto;
}

.notification-modal-header-row .notification-modal-cover-title {
  flex: 1;
  min-width: 0;
}

.notification-badge {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 0 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.notification-badge--unviewed {
  background: rgba(255, 255, 255, 0.96);
  color: #1c1c1e;
}

.notification-badge--viewed {
  background: rgba(28, 28, 30, 0.36);
  color: #fff;
}

.notification-card-body,
.notification-modal-body {
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 22px 22px 24px;
  background: #f5f5f7;
}

.notification-card-preview {
  min-height: 78px;
  margin-bottom: 18px;
  color: #6e6e73;
  font-size: 15px;
  line-height: 1.7;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.notification-card-footer {
  margin-top: auto;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
}

.notification-card-footer-left {
  font-size: 13px;
  color: #8e8e93;
}

.notification-card-date {
  white-space: nowrap;
}

.notification-card-footer-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.notification-card-files-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba(10, 132, 255, 0.08);
  color: #0a84ff;
  font-size: 12px;
  font-weight: 700;
}

.notification-card-action {
  min-width: 0;
  padding-right: 0 !important;
  color: #0a84ff !important;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.notification-modal {
  overflow: hidden;
  border-radius: 20px;
  box-shadow: 0 28px 90px rgba(0, 0, 0, 0.45) !important;
}

.notification-modal-close {
  margin-left: auto;
}

.notification-modal-description {
  white-space: pre-line;
  color: #3a3a3c;
  font-size: 16px;
  line-height: 1.85;
}

.notification-files {
  margin: 22px 0 0;
  padding: 18px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.88);
  border: 1px solid rgba(60, 60, 67, 0.08);
}

.notification-files-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.notification-files-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 700;
  color: #1d1d1f;
}

.notification-files-count {
  min-width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: rgba(10, 132, 255, 0.1);
  color: #0a84ff;
  font-size: 13px;
  font-weight: 700;
}

.notification-files-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 12px;
}

.notification-file-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid rgba(60, 60, 67, 0.08);
  color: inherit !important;
  text-decoration: none;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    border-color 0.2s ease;
}

.notification-file-card:hover {
  transform: translateY(-1px);
  border-color: rgba(10, 132, 255, 0.22);
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.08);
}

.notification-file-icon {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 42px;
  border-radius: 12px;
  color: #0a84ff;
}

.notification-file-icon--pdf {
  background: linear-gradient(135deg, #ffe5e2, #ffd1cb);
  color: #ff453a;
}

.notification-file-icon--sheet {
  background: linear-gradient(135deg, #e5f8ea, #d2f1db);
  color: #30d158;
}

.notification-file-icon--doc {
  background: linear-gradient(135deg, #e4efff, #d5e7ff);
  color: #0a84ff;
}

.notification-file-icon--slides {
  background: linear-gradient(135deg, #fff1dd, #ffe3bd);
  color: #ff9f0a;
}

.notification-file-icon--image {
  background: linear-gradient(135deg, #f2e7ff, #e8d7ff);
  color: #bf5af2;
}

.notification-file-icon--default {
  background: linear-gradient(135deg, #ececf0, #e1e1e6);
  color: #636366;
}

.notification-file-content {
  min-width: 0;
  flex: 1;
}

.notification-file-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 700;
  color: #1d1d1f;
}

.notification-file-caption {
  margin-top: 2px;
  font-size: 12px;
  color: #6e6e73;
}

.notification-file-action {
  color: #0a84ff;
}

.notifications-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 220px;
  border: 1px dashed rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.03);
  color: #f5f5f7;
  text-align: center;
}

.notifications-empty-icon {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.05);
}

.notifications-empty-icon .v-icon {
  color: rgba(255, 255, 255, 0.72) !important;
  font-size: 30px !important;
}

.notifications-empty-title {
  margin-bottom: 6px;
  font-size: 18px;
  font-weight: 700;
}

.notifications-empty-text {
  max-width: 420px;
  color: rgba(255, 255, 255, 0.62);
  font-size: 14px;
  line-height: 1.6;
}

@media (max-width: 1600px) {
  .notifications-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 1100px) {
  .notifications-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .notifications-page {
    margin-top: 20px;
    padding: 0 12px;
  }

  .notifications-heading {
    font-size: 22px;
  }

  .notifications-grid {
    grid-template-columns: 1fr;
  }

  .notification-card-cover-title,
  .notification-modal-cover-title {
    font-size: 20px;
  }

  .notification-modal-header-row {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }

  .notification-card-footer {
    flex-direction: column;
    align-items: flex-start;
  }

  .notification-card-footer-right {
    width: 100%;
    align-items: flex-start;
  }

  .notification-card-badges--footer {
    justify-content: flex-start;
  }

  .notification-card-body,
  .notification-modal-body {
    padding: 18px;
  }

  .notification-modal-description,
  .notification-card-preview {
    font-size: 15px;
  }
}
</style>
